import React, { useEffect, useMemo, useRef, useState } from "react";
import { Tabs } from "@amzn/awsui-components-react";

function parseVersionFromTabId(tabId: string): number {
  return parseInt(tabId.split("-")[1], 10);
}

export interface VersionSelectorProps<T> {
  data: T[];
  getVersion: (item: T) => number;
  renderTabLabel: (version: number, items: T[]) => React.ReactNode;
  renderTabContent: (items: T[]) => React.ReactNode;
  onVersionChange: (index: number) => void;
  initialIndex?: number;
}

export default function VersionSelector<T>(props: VersionSelectorProps<T>) {
  const {
    data,
    getVersion,
    renderTabLabel,
    renderTabContent,
    onVersionChange,
    initialIndex,
  } = props;

  const didInitializeRef = useRef(false);
  const { sortedVersions, versionToIndexMap, versionItemGroups, tabLabels } =
    useMemo(() => {
      const uniqueVersions = Array.from(new Set(data.map(getVersion))).sort(
        (a, b) => b - a
      );

      const indexMap = new Map<number, number>();
      data.forEach((item, index) => {
        const version = getVersion(item);
        if (!indexMap.has(version)) {
          indexMap.set(version, index);
        }
      });

      const itemGroups = new Map<number, T[]>();
      uniqueVersions.forEach((version) => {
        itemGroups.set(
          version,
          data.filter((item) => getVersion(item) === version)
        );
      });

      const labels = uniqueVersions.map((version) => ({
        id: `version-${version}`,
        label: renderTabLabel(version, itemGroups.get(version) || []),
      }));

      return {
        sortedVersions: uniqueVersions,
        versionToIndexMap: indexMap,
        versionItemGroups: itemGroups,
        tabLabels: labels,
      };
    }, [data, getVersion, renderTabLabel]);

  const dataRef = useRef(data);

  const [selectedTabId, setSelectedTabId] = useState<string | undefined>(
    sortedVersions.length > 0 ? `version-${sortedVersions[0]}` : undefined
  );

  useEffect(() => {
    if (sortedVersions.length === 0) return;

    const highestVersion = sortedVersions[0];
    const dataChanged = dataRef.current !== data;

    if (dataChanged || !didInitializeRef.current) {
      let selectedIndex: number;
      let selectedVersion: number;

      if (initialIndex !== undefined && data[initialIndex]) {
        selectedIndex = initialIndex;
        selectedVersion = getVersion(data[initialIndex]);
      } else {
        selectedVersion = highestVersion;
        selectedIndex = versionToIndexMap.get(highestVersion) || 0;
      }

      setSelectedTabId(`version-${selectedVersion}`);
      onVersionChange(selectedIndex);

      if (dataChanged) {
        dataRef.current = data;
      }
      if (!didInitializeRef.current) {
        didInitializeRef.current = true;
      }
    }
  }, [
    data,
    sortedVersions,
    versionToIndexMap,
    onVersionChange,
    initialIndex,
    getVersion,
  ]);

  const handleTabChange = (detail: { activeTabId: string }) => {
    const newTabId = detail.activeTabId;
    setSelectedTabId(newTabId);

    const selectedVersion = parseVersionFromTabId(newTabId);
    const newIndex = versionToIndexMap.get(selectedVersion) || 0;
    onVersionChange(newIndex);
  };

  const activeVersion = selectedTabId
    ? parseVersionFromTabId(selectedTabId)
    : null;
  const activeItems =
    activeVersion !== null ? versionItemGroups.get(activeVersion) : null;

  const tabs = useMemo(
    () =>
      tabLabels.map((t) => ({
        ...t,
        content:
          t.id === selectedTabId && activeItems
            ? renderTabContent(activeItems)
            : null,
      })),
    [tabLabels, selectedTabId, activeItems, renderTabContent]
  );

  return (
    <Tabs
      activeTabId={selectedTabId}
      tabs={tabs}
      onChange={({ detail }) => handleTabChange(detail)}
    />
  );
}
